'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { sendGTMEvent } from '@next/third-parties/google';

import { screen } from '@/components/common/breakpoints';
import {
  coreStyling,
  getButtonStyle,
  getSize,
} from '@/components/common/Buttons';
import { colourCss, measures } from '@/styles';
import { bodyLarge, typography } from '@/styles/typography';

const buttonStyle = () => {
  return `
  ${coreStyling.styles.replace('coreStyling', '')}
    ${getButtonStyle('solid-light').styles.replace('getButtonStyle', '').replaceAll('background', 'background-color')}
    ${getSize('xl').styles.replace('getSize', '')}
    `;
};

export const SalesforceIframe = ({
  src = '',
  formName,
  campaignID,
  iframeID,
}) => {
  const colors = colourCss.styles.toString().replace('colourCss', '');
  const spacing = measures.styles.toString().replace('}measures', '');
  const typographyStyle = typography.styles.replace('typography', '');
  const elRef = useRef<HTMLDivElement>();
  const sendMessage = (iframe) => {
    iframe.contentWindow.postMessage(
      {
        type: 'apply-styles',
        css: ` ${colors}
            ${typographyStyle}
            ${spacing}
            scrollbar-width: none;
            -ms-overflow-style: none;
            &::-webkit-scrollbar {
              display: none;
            }
            body {
              font-feature-settings: var(--font-feature-settings);
              font-family: var(--font-family-saans) !important;
              background-color: var(--colour-white);
              color: var(--text-strong);
            }
            input, select {
              background-color: var(--background-subtle);
              outline: none;
              width: 100% !important;
              border-radius: 8px;
              border: 1px solid var(--border-cool-base);
              min-height: 2.5rem;
              padding:  var(--spacing-xx-small) var(--spacing-x-small);
              color: var(--decorative-dark-ultraviolet);
              ${screen.md} {
                  padding: var(--spacing-xx-small) var(--spacing-300);
                  ${bodyLarge}
              };
              &:focus {
                border-color: var(--decorative-ultraviolet);
              };
            }
            select {
              border: 0;
              outline: 1px solid var(--border-cool-base);
              border-right: var(--spacing-x-small) solid transparent;
              width: 99.4% !important;
              ${screen.sm} {
                width: 99.8% !important;
              }
              &:focus {
                border-color: transparent;
                outline: 0.5px solid var(--decorative-ultraviolet);
              };
            }
            button {
              background-color: unset;
              ${buttonStyle()}
              background: var(--surface-primary) !important;
              color: var(--text-inverse-strong) !important;
              border-radius: var(--radius-full) !important;
              border-color: unset !important;
              padding-left: calc(var(--spacing-250) + var(--padding-offset-left-xl, 0px)) !important;
              padding-right: calc(var(--spacing-250) + var(--padding-offset-right-xl, 0px)) !important;
              margin-top: 33px;
              &:hover {
                background-color: var(--surface-inverse-primary) !important;
              }
            }
            .smartcapture-submit-button-wrapper {
              display: flex;
            }
            .smartcapture-controls {
              display: flex;
              align-items: flex-start;
              flex-direction: column;
              min-width: 100%;
              gap: 8px;
            }
            .sc-formfield-input {
              width: 100%;
            }
            label {
              color: var(--text-warm-subtle) !important;
            }
          `,
      },
      src,
    );
  };

  // Add message event listener to log messages from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const isSFMC =
        typeof event.origin === 'string' &&
        event.origin.endsWith('sfmc-content.com');
      const data = event.data;
      if (
        isSFMC &&
        data?.event === 'sfmc-form-success' &&
        data?.['hs-form-guid']
      ) {
        window?.heap?.track('sfmc-form-success');
        sendGTMEvent({
          event: 'sfmc-form-success',
          'hs-form-guid': data['hs-form-guid'],
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const iframeid = `marketingCloudForm-${iframeID}`;

  useEffect(() => {
    const iframe = window.document.getElementById(iframeid);
    if (iframe) {
      iframe.onload = () => sendMessage(iframe);
    }
  });

  useLayoutEffect(() => {
    const range = document.createRange();
    range.selectNode(elRef.current);
    const currentParams = new URLSearchParams(window.location.search);
    const iframeParams = new URLSearchParams();
    if (currentParams.has('utm_source')) {
      iframeParams.set('utm_source', currentParams.get('utm_source'));
    }
    if (currentParams.has('utm_campaign')) {
      iframeParams.set('utm_campaign', currentParams.get('utm_campaign'));
    }
    if (currentParams.has('utm_medium')) {
      iframeParams.set('utm_medium', currentParams.get('utm_medium'));
    }
    if (currentParams.has('utm_term')) {
      iframeParams.set('utm_term', currentParams.get('utm_term'));
    }
    if (currentParams.has('utm_content')) {
      iframeParams.set('utm_content', currentParams.get('utm_content'));
    }

    iframeParams.set('source_page', 'newsletter_landing_page');
    const form = formName;
    iframeParams.set('Form', form);
    const campaign = campaignID;
    iframeParams.set('CampaignID', campaign);
    iframeParams.set('referrer', document.referrer);
    const fullIframeSrc = iframeParams.toString()
      ? `${src}?${iframeParams.toString()}`
      : src;
    const documentFragment =
      range.createContextualFragment(`<div class="form-container">
          <iframe
          id="marketingCloudForm-${iframeID}"
          src="${fullIframeSrc}"
          frameborder="0"
          srolling="no"
        ></iframe></div>
        `);

    elRef.current.append(documentFragment);
  }, []);

  return (
    <Container>
      <div
        ref={elRef}
        dangerouslySetInnerHTML={{
          __html: `
          <!DOCTYPE html>
          <html lang="en">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              iframe { width: 100%; height: 690px; border: none; overflow: hidden; }
            </style>
          </head>
          </html>
        `,
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  max-width: 400px;
  margin: 0 auto;
  ${screen.sm} {
    max-width: 600px;
  }
  ${screen.md} {
    max-width: 800px;
  }
  ${screen.lg} {
    max-width: 1000px;
  }
`;
